import React, { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import { mediaApi } from "../../../media/mediaApi";
import { downloadFileExtension, imageHasOriginalChoice } from "./downloadFileExtension";
import { triggerDownload } from "../../../helpers/collectorQrDownload";

type Pending = { url: string; type: string; name: string; assetId: string };

async function saveUrl(url: string, filename: string) {
  const response = await fetch(url, { method: "GET", cache: "no-cache" });
  if (!response.ok) throw new Error("Download failed");
  const blobUrl = URL.createObjectURL(new Blob([await response.arrayBuffer()]));
  triggerDownload(blobUrl, filename);
  URL.revokeObjectURL(blobUrl);
}

const DownloadIcon = (props: any) => {
  const { item, i, name, image } = props;
  const [pending, setPending] = useState<Pending | null>(null);
  const [archiveStatus, setArchiveStatus] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!pending?.assetId) return undefined;
    let current = true;
    setArchiveStatus("");
    void mediaApi.get(pending.assetId).then(view => {
      if (current) setArchiveStatus(view.archiveStatus || "");
    }).catch(() => undefined);
    return () => { current = false; };
  }, [pending]);

  const close = () => { if (!busy) { setPending(null); setError(""); } };
  const originalReady = !archiveStatus || archiveStatus === "ORIGINAL_ARCHIVED";

  const run = async (kind: "original" | "jpg") => {
    if (!pending) return;
    setBusy(kind);
    setError("");
    try {
      if (kind === "jpg") {
        const ext = downloadFileExtension("image/jpeg", pending.url);
        await saveUrl(pending.url, `${pending.name}${new Date().getSeconds()}.${ext}`);
      } else {
        const saved = await mediaApi.downloadOriginal(pending.assetId);
        const filename = saved.fileName || `original.${downloadFileExtension(saved.contentType, saved.fileName)}`;
        await saveUrl(saved.url, filename);
      }
      setPending(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setBusy("");
    }
  };

  const start = () => {
    const url = item?.blobImage?.length ? item.blobImage : image;
    const type = item?.type?.length > 0 ? item.type : `image.${String(image || "").split(".")[1] || "jpeg"}`;
    const assetId = String(item?.mediaAssetId || "");
    if (imageHasOriginalChoice({ mediaAssetId: assetId, type })) {
      setError("");
      setPending({ url, type, name: name || "photo", assetId });
      return;
    }
    const ext = downloadFileExtension(type, url);
    void saveUrl(url, `${name || "photo"}${new Date().getSeconds()}.${ext}`).catch(() => undefined);
  };

  return (
    <>
      <svg
        key={i}
        onClick={start}
        className={props.placePosition === "first" ? "DownloadIcon1" : "DownloadIcon"}
        width="25"
        height="25"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="20" height="25" rx="1" fill="#FFF"></rect>
        <path d="M9 7V8.33333C9 8.51014 8.92976 8.67971 8.80474 8.80474C8.67971 8.92976 8.51014 9 8.33333 9H3.66667C3.48986 9 3.32029 8.92976 3.19526 8.80474C3.07024 8.67971 3 8.51014 3 8.33333V7" fill="#FFF"></path>
        <path d="M9 7V8.33333C9 8.51014 8.92976 8.67971 8.80474 8.80474C8.67971 8.92976 8.51014 9 8.33333 9H3.66667C3.48986 9 3.32029 8.92976 3.19526 8.80474C3.07024 8.67971 3 8.51014 3 8.33333V7" stroke="#404B3B" strokeLinecap="round" strokeLinejoin="round"></path>
        <path d="M4.33301 5.33331L5.99967 6.99998L7.66634 5.33331" fill="#FFF"></path>
        <path d="M4.33301 5.33331L5.99967 6.99998L7.66634 5.33331" stroke="#404B3B" strokeLinecap="round" strokeLinejoin="round"></path>
        <path d="M6 7V3" stroke="#404B3B" strokeLinecap="round" strokeLinejoin="round"></path>
      </svg>
      <Modal isOpen={Boolean(pending)} toggle={close} fade={false} className="hkp-download-choice">
        <ModalHeader toggle={close}>Download photo</ModalHeader>
        <ModalBody>
          <p>Choose the original photograph, or the smaller JPG used in the product.</p>
          <Button color="primary" disabled={Boolean(busy) || !originalReady} onClick={() => void run("original")}>
            {busy === "original" ? "Preparing original…" : "Original file"}
          </Button>
          <Button color="secondary" disabled={Boolean(busy)} onClick={() => void run("jpg")}>
            {busy === "jpg" ? "Preparing JPG…" : "Compressed JPG"}
          </Button>
          {!originalReady && <p className="hkp-download-choice__note">The original file is still uploading. The compressed JPG is ready.</p>}
          {error && <p className="hkp-download-choice__note">{error}</p>}
        </ModalBody>
      </Modal>
    </>
  );
};

export default DownloadIcon;
